import _ from 'lodash';
import moment from 'moment';

import env from '@/config/env';
import { CoreDates } from '@/utils/core/data-transformation';
import { CoreEnums, reverseEnumerator } from '@/utils/domain/constants';

const {
  CustomListCommandType,
  CustomListCommandStringType,
  RequestDetailCommandType,
  ActionCustomCommandType,
  FilterDateRange,
} = CoreEnums;

const REQUEST_CMD_STRING_TO_NUMERIC = {
  ..._.mapKeys(RequestDetailCommandType, (__, key) => _.kebabCase(key)),
  'view-details': RequestDetailCommandType.Details,
  'cancel-request': RequestDetailCommandType.Cancel,
  'void-request': RequestDetailCommandType.Void,
  'approve-request': RequestDetailCommandType.Approve,
  'reject-request': RequestDetailCommandType.Reject,
  'complete-request-information': RequestDetailCommandType.Complete,
  'sign-document': RequestDetailCommandType.Sign,
};

const ACTION_CUSTOM_CMD_STRING_TO_NUMERIC = _.mapKeys(ActionCustomCommandType || {}, (__, key) =>
  _.kebabCase(key),
);

const normalizeEnumValue = (value, mapping) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const mapped = mapping[_.kebabCase(value)];
    return _.isNumber(mapped) ? mapped : value;
  }
  return value;
};

export const normalizeRequestCommand = (cmd) =>
  normalizeEnumValue(cmd?.command, REQUEST_CMD_STRING_TO_NUMERIC);

export const normalizeCustomCommandType = (cmd) =>
  normalizeEnumValue(cmd?.customCommandType, ACTION_CUSTOM_CMD_STRING_TO_NUMERIC);

const ROW_COMMAND_ORDER = {
  [RequestDetailCommandType.Details]: 10,
  [RequestDetailCommandType.Continue]: 20,
  [RequestDetailCommandType.Approve]: 30,
  [RequestDetailCommandType.Complete]: 35,
  [RequestDetailCommandType.ExecuteTask]: 40,
  [RequestDetailCommandType.Sign]: 45,
  [RequestDetailCommandType.ReassignTask]: 60,
  [RequestDetailCommandType.Reject]: 80,
  [RequestDetailCommandType.Cancel]: 90,
  [RequestDetailCommandType.Void]: 95,
};

const CUSTOM_CMD_ORDER_OFFSET = {
  [ActionCustomCommandType.Approve]: 50,
  [ActionCustomCommandType.Skip]: 65,
  [ActionCustomCommandType.Reject]: 85,
};

const orderForRowCommand = (cmd) => {
  const command = normalizeRequestCommand(cmd);
  if (command === RequestDetailCommandType.GoToActionFromCustomCommand) {
    const ct = normalizeCustomCommandType(cmd);
    return CUSTOM_CMD_ORDER_OFFSET[ct] ?? 70;
  }
  return ROW_COMMAND_ORDER[command] ?? 100;
};

export const sortRowCommands = (commands = []) => {
  return _.sortBy(commands, [(c) => orderForRowCommand(c), (c) => commands.indexOf(c)]);
};

export const HERO_COMMANDS = [
  CustomListCommandType.CreateRequest,
  CustomListCommandType.EntityAdd,
  CustomListCommandType.EntityImport,
  CustomListCommandType.Export,
  CustomListCommandType.MassRequestsApprove,
  CustomListCommandType.MassRequestsReject,
  CustomListCommandType.MassRequestsCancel,
  CustomListCommandType.MassRequestsVoid,
  CustomListCommandType.MassRequestsExecuteTask,
  CustomListCommandType.MassRequestsCompleteRequestInformation,
  CustomListCommandStringType.CreateRequest,
  CustomListCommandStringType.EntityAdd,
  CustomListCommandStringType.EntityImport,
  CustomListCommandStringType.Export,
  CustomListCommandStringType.MassRequestsApprove,
  CustomListCommandStringType.MassRequestsReject,
  CustomListCommandStringType.MassRequestsCancel,
  CustomListCommandStringType.MassRequestsVoid,
  CustomListCommandStringType.MassRequestsExecuteTask,
  CustomListCommandStringType.MassRequestsCompleteRequestInformation,
];

export const BULK_HERO_COMMANDS = [
  CustomListCommandType.MassRequestsApprove,
  CustomListCommandType.MassRequestsReject,
  CustomListCommandType.MassRequestsCancel,
  CustomListCommandType.MassRequestsVoid,
  CustomListCommandType.MassRequestsExecuteTask,
  CustomListCommandType.MassRequestsCompleteRequestInformation,
  CustomListCommandStringType.MassRequestsApprove,
  CustomListCommandStringType.MassRequestsReject,
  CustomListCommandStringType.MassRequestsCancel,
  CustomListCommandStringType.MassRequestsVoid,
  CustomListCommandStringType.MassRequestsExecuteTask,
  CustomListCommandStringType.MassRequestsCompleteRequestInformation,
];

// Row-level command(s) a selected row must expose for a given mass
// command to be eligible against it. A value may be a single
// `RequestDetailCommandType` or an array (row must expose ANY of them).
// `mass-requests-complete-request-information` matches rows whose
// pending sub-command is `continue`, `complete` OR `reject`.
export const BULK_REQUIRED_ROW_COMMAND = {
  [CustomListCommandType.MassRequestsApprove]: RequestDetailCommandType.Approve,
  [CustomListCommandType.MassRequestsReject]: RequestDetailCommandType.Reject,
  [CustomListCommandType.MassRequestsCancel]: RequestDetailCommandType.Cancel,
  [CustomListCommandType.MassRequestsVoid]: RequestDetailCommandType.Void,
  [CustomListCommandType.MassRequestsExecuteTask]: RequestDetailCommandType.ExecuteTask,
  [CustomListCommandType.MassRequestsCompleteRequestInformation]: [
    RequestDetailCommandType.Complete,
    RequestDetailCommandType.Continue,
    RequestDetailCommandType.Reject,
  ],
  [CustomListCommandStringType.MassRequestsApprove]: RequestDetailCommandType.Approve,
  [CustomListCommandStringType.MassRequestsReject]: RequestDetailCommandType.Reject,
  [CustomListCommandStringType.MassRequestsCancel]: RequestDetailCommandType.Cancel,
  [CustomListCommandStringType.MassRequestsVoid]: RequestDetailCommandType.Void,
  [CustomListCommandStringType.MassRequestsExecuteTask]: RequestDetailCommandType.ExecuteTask,
  [CustomListCommandStringType.MassRequestsCompleteRequestInformation]: [
    RequestDetailCommandType.Complete,
    RequestDetailCommandType.Continue,
    RequestDetailCommandType.Reject,
  ],
};

export const BULK_ELIGIBILITY_MODE = {
  [CustomListCommandType.MassRequestsApprove]: 'every',
  [CustomListCommandType.MassRequestsReject]: 'every',
  [CustomListCommandType.MassRequestsCancel]: 'every',
  [CustomListCommandType.MassRequestsVoid]: 'some',
  [CustomListCommandType.MassRequestsExecuteTask]: 'every',
  [CustomListCommandType.MassRequestsCompleteRequestInformation]: 'every',
  [CustomListCommandStringType.MassRequestsApprove]: 'every',
  [CustomListCommandStringType.MassRequestsReject]: 'every',
  [CustomListCommandStringType.MassRequestsCancel]: 'every',
  [CustomListCommandStringType.MassRequestsVoid]: 'some',
  [CustomListCommandStringType.MassRequestsExecuteTask]: 'every',
  [CustomListCommandStringType.MassRequestsCompleteRequestInformation]: 'every',
};

// True when a row exposes (any of) the row-level command(s) `required`
// by a mass command. Accepts a single `RequestDetailCommandType` or an
// array of them.
export const rowMatchesBulkCommand = (row, required) => {
  const targets = Array.isArray(required) ? required : [required];
  return !!row?.commands?.some((c) => targets.includes(normalizeRequestCommand(c)));
};

export const isPrimaryCommand = (cmd) =>
  cmd === CustomListCommandType.CreateRequest ||
  cmd === CustomListCommandType.EntityAdd ||
  cmd === CustomListCommandStringType.CreateRequest ||
  cmd === CustomListCommandStringType.EntityAdd;

export const isSuccessCommand = (cmd) =>
  cmd === CustomListCommandType.MassRequestsApprove ||
  cmd === CustomListCommandStringType.MassRequestsApprove;

export const isDangerCommand = (cmd) =>
  cmd === CustomListCommandType.MassRequestsReject ||
  cmd === CustomListCommandType.MassRequestsCancel ||
  cmd === CustomListCommandType.MassRequestsVoid ||
  cmd === CustomListCommandStringType.MassRequestsReject ||
  cmd === CustomListCommandStringType.MassRequestsCancel ||
  cmd === CustomListCommandStringType.MassRequestsVoid;

export const defaultHiddenCommands = _.zipObject(
  BULK_HERO_COMMANDS,
  BULK_HERO_COMMANDS.map(() => true),
);

export const isBulkCommand = (cmd) => BULK_HERO_COMMANDS.includes(cmd);

export const hasBulkCommands = (commands = []) =>
  _.some(commands, (c) => isBulkCommand(c?.command));

export const isExportCommand = (cmd) =>
  cmd === CustomListCommandType.Export || cmd === CustomListCommandStringType.Export;

export const isEntityImportCommand = (cmd) =>
  cmd === CustomListCommandType.EntityImport || cmd === CustomListCommandStringType.EntityImport;

// Every `entity-*` command (Add/Edit/Delete/Import) is only meaningful
// for lists backed by a dynamic entity. Use this guard to hide them on
// process-origin lists where they would no-op or hit invalid routes.
const ENTITY_ORIGIN_COMMANDS = new Set([
  CustomListCommandType.EntityAdd,
  CustomListCommandType.EntityEdit,
  CustomListCommandType.EntityDelete,
  CustomListCommandType.EntityImport,
  CustomListCommandStringType.EntityAdd,
  CustomListCommandStringType.EntityEdit,
  CustomListCommandStringType.EntityDelete,
  CustomListCommandStringType.EntityImport,
]);

export const isEntityOriginCommand = (cmd) => ENTITY_ORIGIN_COMMANDS.has(cmd);

const DATE_RANGE_FALLBACK_KEY = 'last_30_days';

// `defaultDateRange` may arrive as the backend snake_case string
// (e.g. "current_year") straight from `list/getConfig`, or as the
// numeric `FilterDateRange` enum value. Normalize both into the
// snake_case key used by `CoreDates.DATE_RANGE_VALUES`.
export const resolveDateRangeKey = (defaultDateRange) => {
  if (defaultDateRange == null) return DATE_RANGE_FALLBACK_KEY;

  let key;
  if (typeof defaultDateRange === 'string') {
    key = _.snakeCase(defaultDateRange);
  } else {
    const byDate = reverseEnumerator(FilterDateRange, { case: 'snake' });
    key = byDate[defaultDateRange];
  }

  // `Default` (0) has no real range; treat it as the app fallback.
  if (!key || key === 'default') return DATE_RANGE_FALLBACK_KEY;
  return key;
};

export const isDisabledDateRange = (defaultDateRange) =>
  defaultDateRange === FilterDateRange.Disabled ||
  (typeof defaultDateRange === 'string' && _.snakeCase(defaultDateRange) === 'disabled');

export const buildInitialDateRange = (defaultDateRange) => {
  if (isDisabledDateRange(defaultDateRange)) return null;

  const key = resolveDateRangeKey(defaultDateRange);
  const range = CoreDates.DATE_RANGE_VALUES[key];
  return range || null;
};

export const isDefaultDateRange = (range, defaultDateRange) => {
  if (isDisabledDateRange(defaultDateRange)) return !range;
  if (!Array.isArray(range) || range.length !== 2) return false;

  const fallback = buildInitialDateRange(defaultDateRange);
  if (!fallback) return false;

  const [a, b] = range;
  const [da, db] = fallback;

  return moment.isMoment(a) && moment.isMoment(b)
    ? a.isSame(da, 'day') && b.isSame(db, 'day')
    : false;
};

const toSafeText = (raw, fallback) => {
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
  if (fallback != null && typeof fallback !== 'object') return String(fallback);
  return '';
};

export const filtersToBackendBody = (
  localFilters = {},
  dynamicFilters = {},
  filtersConfig = [],
) => {
  const body = {};

  if (localFilters.startDate) body.startDate = localFilters.startDate;
  if (localFilters.endDate) body.endDate = localFilters.endDate;
  if (localFilters.instanceStatus != null) body.instanceStatus = localFilters.instanceStatus;
  if (localFilters.applicantUserId) body.applicantUserId = localFilters.applicantUserId;

  const cfgByName = _.keyBy(filtersConfig, 'name');
  const filters = [];

  _.forEach(dynamicFilters, (value, internalName) => {
    if (value == null || value === '') return;

    const cfg = cfgByName[internalName];
    const fieldId = cfg?.id ?? internalName;
    const isDateLike =
      cfg?.type === 'datepicker' ||
      cfg?.type === 'date-filter' ||
      cfg?.knownType === 'Date' ||
      cfg?.knownType === 'DateTime';

    if (Array.isArray(value)) {
      if (value.length === 0) return;

      if (isDateLike && value.length === 2) {
        const [a, b] = value;
        const startMoment = moment.isMoment(a) ? a : moment(a);
        const endMoment = moment.isMoment(b) ? b : moment(b);
        if (!startMoment.isValid() || !endMoment.isValid()) return;
        filters.push({
          fieldId,
          filterStartValue: startMoment.format(env.DATE_RANGE_START_FORMAT),
          filterEndValue: endMoment.endOf('day').format(env.DATE_RANGE_END_FORMAT),
        });
        return;
      }

      _.forEach(value, (entry) => {
        if (entry == null || entry === '') return;
        if (typeof entry === 'object') {
          const id = entry.value ?? entry.key ?? entry.id;
          const rawText = entry.label ?? entry.text ?? entry.name;
          if (id == null || id === '') return;
          filters.push({ fieldId, filterValue: { id, text: toSafeText(rawText, id) } });
          return;
        }
        filters.push({ fieldId, filterValue: { id: entry, text: toSafeText(entry, entry) } });
      });
      return;
    }

    if (typeof value === 'object') {
      const id = value.value ?? value.key ?? value.id;
      const rawText = value.label ?? value.text ?? value.name;
      if (id == null || id === '') return;
      filters.push({ fieldId, filterValue: { id, text: toSafeText(rawText, id) } });
      return;
    }

    filters.push({ fieldId, filterValue: { id: value, text: toSafeText(value, value) } });
  });

  if (filters.length > 0) body.filters = filters;
  return Object.keys(body).length > 0 ? body : null;
};

// Build cascade body for a given filter dropdown: same shape as data fetch,
// but excluding the target filter so its options narrow against siblings.
export const buildFilterCascadeBody = (
  localFilters = {},
  dynamicFilters = {},
  filtersConfig = [],
  excludedName = null,
) => {
  const filteredDynamic = excludedName ? _.omit(dynamicFilters, excludedName) : dynamicFilters;
  return filtersToBackendBody(localFilters, filteredDynamic, filtersConfig) || {};
};

// Stable signature for a cascade body so callers can invalidate caches on change.
export const cascadeSignature = (cascadeBody) => {
  if (!cascadeBody) return '';
  try {
    return JSON.stringify(cascadeBody);
  } catch (e) {
    return '';
  }
};

export const dateRangeToFilterPair = (range) => {
  if (!Array.isArray(range) || range.length !== 2) {
    return { startDate: undefined, endDate: undefined };
  }
  const [start, end] = range;
  if (!start || !end) return { startDate: undefined, endDate: undefined };

  const startMoment = moment.isMoment(start) ? start : moment(start);
  const endMoment = moment.isMoment(end) ? end : moment(end);

  return {
    startDate: startMoment.format(env.DATE_RANGE_START_FORMAT),
    endDate: endMoment.endOf('day').format(env.DATE_RANGE_END_FORMAT),
  };
};

export const storageKey = (listId) => `lists-modern.${listId || 'default'}.session-state`;

export const readPersistedState = (listId) => {
  try {
    if (typeof window === 'undefined' || !listId) return null;
    const raw = window.sessionStorage.getItem(storageKey(listId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);

    if (Array.isArray(parsed?.dateRange)) {
      parsed.dateRange = parsed.dateRange.map((s) => (s ? moment(s) : null)).filter(Boolean);
      if (parsed.dateRange.length !== 2) delete parsed.dateRange;
    }

    return parsed;
  } catch (e) {
    return null;
  }
};

export const writePersistedState = (listId, state) => {
  try {
    if (typeof window === 'undefined' || !listId) return;
    const payload = {
      ...state,
      dateRange: Array.isArray(state.dateRange)
        ? state.dateRange.map((m) => (moment.isMoment(m) ? m.toISOString() : m))
        : undefined,
    };
    window.sessionStorage.setItem(storageKey(listId), JSON.stringify(payload));
  } catch (e) {
    /* no-op */
  }
};

export const clearPersistedState = (listId) => {
  try {
    if (typeof window === 'undefined' || !listId) return;
    window.sessionStorage.removeItem(storageKey(listId));
  } catch (e) {
    /* no-op */
  }
};

export const businessContextSelector = (state) => {
  const ctx = state?.userContext;
  if (!ctx) return '';
  const c = ctx.currentCompany;
  const e = ctx.currentEnvironment;
  const companyId = c && typeof c === 'object' ? c.id : c;
  const envId = e && typeof e === 'object' ? e.id : e;
  return [companyId, envId].map((v) => (v != null && v !== '' ? String(v) : '')).join('|');
};
